import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Video } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

// Customer video reviews land here. Send a clip and it becomes a playable tile:
// { src: "/reviews/review-N.mp4", poster: "/reviews/review-N.jpg", name: "Rider name", route: "Raipur → Kolkata" }
const REVIEWS = [
  { src: "/reviews/review-1.mp4", poster: "/reviews/review-1.jpg", name: "Customer review", route: "Night run" },
  { src: "/reviews/review-2.mp4", poster: "/reviews/review-2.jpg", name: "Customer review", route: "In the back seat" },
  { src: "/reviews/review-3.mp4", poster: "/reviews/review-3.jpg", name: "Customer review", route: "Airport → Tata Steel, Duburi" },
  { src: "/reviews/review-4.mp4", poster: "/reviews/review-4.jpg", name: "Customer review", route: "Book on WhatsApp" },
  { src: "/reviews/review-5.mp4", poster: "/reviews/review-5.jpg", name: "Customer review", route: "Balasore → Bhubaneswar" },
];

const SLOT_COUNT = 3;

function EmptyTile({ index }) {
  return (
    <div
      data-testid={`review-slot-${index + 1}`}
      className="aspect-[3/4] border border-dashed border-white/20 bg-white/[0.02] flex flex-col items-center justify-center gap-4 text-center px-6"
    >
      <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-white/15 text-zinc-600">
        <Video size={22} strokeWidth={1.6} />
      </span>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500">Customer review</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600 mt-1.5">Rolling in soon</p>
      </div>
    </div>
  );
}

function ReviewTile({ r, index }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div data-testid={`review-tile-${index + 1}`} className="relative aspect-[3/4] border border-white/10 bg-surface overflow-hidden group">
      {playing ? (
        <video src={r.src} controls autoPlay playsInline className="absolute inset-0 w-full h-full object-contain bg-ink" />
      ) : (
        <>
          <img src={r.poster} alt={`${r.name} review`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
          <button
            data-testid={`review-play-${index + 1}`}
            onClick={() => setPlaying(true)}
            aria-label={`Play review from ${r.name}`}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-signal text-ink hover:scale-110 transition-transform duration-300">
              <Play size={22} fill="currentColor" className="ml-0.5" />
            </span>
          </button>
          <div className="absolute bottom-0 inset-x-0 p-5">
            <p className="font-display uppercase text-xl text-bone">{r.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mt-1">{r.route}</p>
          </div>
        </>
      )}
    </div>
  );
}

export default function Reviews() {
  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="mb-14 md:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal mb-4">
            (05) — Voices from the back seat
          </p>
          <h2 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Riders,
            <br />
            on record.
          </h2>
        </div>
        <p className="max-w-sm text-zinc-400 leading-relaxed text-base">
          Straight from our customers' phones — short video reviews of real
          trips. No scripts, no actors.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {REVIEWS.length > 0
          ? REVIEWS.map((r, i) => <ReviewTile key={i} r={r} index={i} />)
          : Array.from({ length: SLOT_COUNT }, (_, i) => <EmptyTile key={i} index={i} />)}
      </div>
    </section>
  );
}
